import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Receipt, Ticket as TicketIcon, ShareNetwork, LinkSimple, MapPin, CalendarBlank, Clock, ShieldCheck } from "@phosphor-icons/react";
import { PageLoader, EmptyState, StatusBadge, SmartImage } from "../../components/shared";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import { toast } from "sonner";
import api, { money } from "../../lib/api";
import { useAutoRefresh } from "../../hooks/useAutoRefresh";
import { RefreshBar } from "../../components/RefreshBar";

const TABS = [["upcoming", "Upcoming"], ["completed", "Completed"], ["cancelled", "Cancelled"], ["refunded", "Refunded"]];

function fmtDate(d) {
  if (!d) return "TBA";
  try { return new Date(d).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" }); }
  catch { return d; }
}

export default function MyBookings() {
  const [view, setView] = useState("bookings"); // bookings | tickets
  const [bookings, setBookings] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ticketsLoaded, setTicketsLoaded] = useState(false);
  const [tab, setTab] = useState("upcoming");
  const [active, setActive] = useState(null);

  const load = () => api.get("/bookings").then((r) => setBookings(r.data.bookings)).finally(() => setLoading(false));
  const { refreshing, lastUpdated, refresh } = useAutoRefresh(load, []);

  useEffect(() => {
    if (view === "tickets" && !ticketsLoaded) {
      api.get("/tickets").then((r) => { setTickets(r.data.tickets); setTicketsLoaded(true); });
    }
  }, [view, ticketsLoaded]);

  const filtered = bookings.filter((b) => b.group === tab);
  const shareLink = (t) => `${window.location.origin}/t/${t.share_token}`;
  const nativeShare = async (t) => {
    const url = shareLink(t);
    try { if (navigator.share) await navigator.share({ title: `${t.event_title} — EventVexa Ticket`, text: `My ticket for ${t.event_title}`, url }); else { await navigator.clipboard.writeText(url); toast.success("Ticket link copied."); } } catch {}
  };
  const copyLink = async (t) => { try { await navigator.clipboard.writeText(shareLink(t)); toast.success("Secure ticket link copied."); } catch { toast.error("Couldn't copy link."); } };

  return (
    <div className="mx-auto max-w-5xl px-4 md:px-6 py-8">
      <RefreshBar refreshing={refreshing} lastUpdated={lastUpdated} onRefresh={refresh} />
      <div className="overline mb-1">Your activity</div>
      <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight mb-6">My Bookings</h1>

      <div className="flex gap-2 mb-6 p-1 rounded-full bg-muted w-fit">
        {[["bookings", "Bookings", Receipt], ["tickets", "Tickets", TicketIcon]].map(([v, l, Icon]) => (
          <button key={v} onClick={() => setView(v)} data-testid={`view-${v}`}
            className={`inline-flex items-center gap-1.5 px-5 h-9 rounded-full text-sm font-semibold transition-colors ${view === v ? "bg-foreground text-background" : "text-muted-foreground"}`}>
            <Icon size={15} weight={view === v ? "fill" : "regular"} /> {l}
          </button>
        ))}
      </div>

      {view === "bookings" ? (
        <>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-6 flex-wrap h-auto">
              {TABS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`tab-${v}`}>{l}</TabsTrigger>)}
            </TabsList>
          </Tabs>
          {loading ? <PageLoader /> : filtered.length === 0 ? (
            <EmptyState icon={Receipt} title={`No ${tab} bookings`} subtitle="When you book an event it will appear here."
              action={<Button asChild className="rounded-full"><Link to="/discover">Explore events</Link></Button>} testid="no-bookings" />
          ) : (
            <div className="space-y-4">
              {filtered.map((b) => (
                <Link key={b.id} to={`/bookings/${b.id}`} data-testid={`booking-${b.id}`}
                  className="flex gap-4 p-3 rounded-xl border border-border bg-card card-lift">
                  <div className="w-24 sm:w-32 shrink-0"><SmartImage src={b.event_banner} alt={b.event_title} ratio="video" /></div>
                  <div className="flex-1 min-w-0 py-1">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-heading font-semibold text-lg tracking-tight truncate">{b.event_title}</h3>
                      <StatusBadge status={b.event_lifecycle_status === "cancelled" ? "cancelled" : b.status} />
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">{b.show_date} · {b.city}</div>
                    {b.event_status_label && (
                      <div className="mt-1 text-xs font-semibold text-amber-300" data-testid={`booking-event-status-${b.id}`}>
                        {b.event_status_label}
                      </div>
                    )}
                    <div className="mt-1 text-xs text-muted-foreground">Booking ID: {b.id}</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Ticket type: {(b.line_items || []).map((item) => item.name).join(", ") || "—"}
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-sm inline-flex items-center gap-1.5"><TicketIcon size={15} /> {b.ticket_count} ticket(s)</span>
                      <span className="font-semibold">{money(b.total)}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </>
      ) : (
        !ticketsLoaded ? <PageLoader /> : tickets.length === 0 ? (
          <EmptyState icon={TicketIcon} title="No tickets yet" subtitle="Book an event to get your QR M-tickets."
            action={<Button asChild className="rounded-full"><Link to="/discover">Explore events</Link></Button>} testid="no-tickets" />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {tickets.map((t) => (
              <div key={t.id} data-testid={`mticket-${t.id}`} className="rounded-xl border border-border bg-card overflow-hidden card-lift">
                <div className="bg-foreground text-background p-4">
                  <div className="text-xs uppercase tracking-widest text-background/60">{t.category}</div>
                  <div className="font-heading font-semibold text-lg tracking-tight truncate">{t.event_title}</div>
                  <div className="text-xs text-background/70 mt-0.5">{fmtDate(t.show_date)} · {t.city}</div>
                </div>
                <button onClick={() => setActive(t)} data-testid={`open-ticket-${t.id}`} className="w-full p-5 grid place-items-center hover:bg-muted/40 transition-colors">
                  <img src={t.qr} alt="QR" className="w-36 h-36" />
                  <div className="font-mono text-sm mt-2">{t.code}</div>
                  <div className="mt-2"><StatusBadge status={t.checked_in ? "verified" : t.status} /></div>
                  <div className="mt-2 text-xs text-primary font-semibold">Tap to open & share</div>
                </button>
              </div>
            ))}
          </div>
        )
      )}

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="max-w-sm p-0 overflow-hidden gap-0" data-testid="ticket-modal">
          {active && (
            <div>
              <div className="bg-foreground text-background p-5 relative">
                <div className="text-xs uppercase tracking-widest text-primary font-bold">{active.category}</div>
                <div className="font-heading text-xl font-semibold tracking-tight mt-0.5 pr-6">{active.event_title}</div>
                <div className="text-sm text-background/70 mt-1">{active.occurrence_label}</div>
              </div>
              <div className="p-6 grid place-items-center bg-white">
                <img src={active.qr} alt="QR ticket" className="w-60 h-60" data-testid="ticket-fullscreen-qr" />
                <div className="font-mono text-base mt-3 tracking-wide">{active.code}</div>
                <div className="mt-2"><StatusBadge status={active.checked_in ? "verified" : active.status} /></div>
              </div>
              <div className="px-6 pb-4 space-y-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground"><CalendarBlank size={15} className="text-primary" /> {fmtDate(active.show_date)}</div>
                <div className="flex items-center gap-2 text-muted-foreground"><Clock size={15} className="text-primary" /> {active.show_time || "TBA"}</div>
                <div className="flex items-center gap-2 text-muted-foreground"><MapPin size={15} className="text-primary" /> {active.venue}, {active.city}</div>
                {active.access_checkpoints?.length > 0 && (
                  <div className="pt-2">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1"><ShieldCheck size={13} /> Access</div>
                    <div className="flex flex-wrap gap-1.5">
                      {active.access_checkpoints.map((c, i) => (
                        <span key={i} className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold">{c}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="p-4 border-t border-border flex gap-2">
                <Button onClick={() => nativeShare(active)} className="flex-1 rounded-full" data-testid="ticket-share-btn"><ShareNetwork className="mr-1.5" /> Share</Button>
                <Button variant="outline" onClick={() => copyLink(active)} className="flex-1 rounded-full" data-testid="ticket-copy-link"><LinkSimple className="mr-1.5" /> Copy link</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
